import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { equipmentRequestsListService } from '../services/equipmentRequestsList.service';
import type { EquipmentRequestFilters } from '../types/equipmentRequests.types';

export function useEquipmentRequests() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const [filters, setFilters] = useState<EquipmentRequestFilters>({ query: '', status: 'all' });

  const query = useQuery({
    queryKey: ['equipment', 'requests', 'list', companyId],
    queryFn: () => equipmentRequestsListService.getEquipmentRequests(companyId, userId),
    enabled: !!companyId && !!userId,
  });

  const stats = useMemo(() => {
    const data = query.data || [];
    return {
      total: data.length,
      requested: data.filter(r => r.statusCode === 'requested').length,
      approved: data.filter(r => r.statusCode === 'approved').length,
      inUse: data.filter(r => r.statusCode === 'in_use').length,
    };
  }, [query.data]);

  const filteredRequests = useMemo(() => {
    const search = filters.query.toLowerCase().trim();
    return (query.data || []).filter(item => {
      const matchesSearch = !search || 
        item.title.toLowerCase().includes(search) || 
        item.project?.name.toLowerCase().includes(search) ||
        item.requester?.full_name.toLowerCase().includes(search);
      const matchesStatus = filters.status === 'all' || item.statusCode === filters.status;
      return matchesSearch && matchesStatus;
    });
  }, [query.data, filters]);

  return {
    requests: filteredRequests,
    stats,
    filters,
    isLoading: query.isLoading,
    setFilters,
  };
}
